// iter564/iter566 — Extracted from BankStatementsPage.jsx
// Renders the "Orphan Inbox" card: a list of downstream records
// (bank_payments, expenses, platform_payments) whose source bank
// statement was deleted. Each row offers Rebind / Detach actions,
// plus a "Rebind all matching" bulk action in the header.

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, Check, X } from 'lucide-react';

const TYPE_META = {
  bank_payment: { label: 'PAYMENT', cls: 'bg-violet-100 text-violet-700' },
  expense: { label: 'EXPENSE', cls: 'bg-blue-100 text-blue-700' },
  platform_payment: { label: 'POS', cls: 'bg-emerald-100 text-emerald-700' },
};

export function OrphanInboxCard({
  orphans,
  expanded,
  busy,
  onToggleExpand,
  onRebind,
  onRebindAllMatching,
  onDetach,
}) {
  if (!orphans || (orphans.total || 0) === 0) return null;
  return (
    <Card className="border-amber-300 bg-amber-50/40" data-testid="orphan-inbox-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-outfit text-base flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            Orphan Inbox
            <Badge variant="secondary" className="font-normal" data-testid="orphan-total-badge">{orphans.total}</Badge>
            {orphans.rebindable > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 font-normal" data-testid="orphan-rebindable-badge">
                {orphans.rebindable} ready to rebind
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            {orphans.rebindable > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                onClick={onRebindAllMatching}
                disabled={busy}
                data-testid="orphan-rebind-all-matching"
                title={`Re-bind all ${orphans.rebindable} orphans whose original file is already re-uploaded`}
              >
                Rebind all matching ({orphans.rebindable})
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={onToggleExpand}
              data-testid="orphan-toggle"
            >
              {expanded ? 'Hide' : 'Review'}
            </Button>
          </div>
        </CardTitle>
        {!expanded && (
          <div className="text-[11px] text-muted-foreground">
            These payments/expenses survived a statement deletion. Re-upload the original file and click &quot;Rebind&quot; to wire them back automatically. Or detach permanently to keep them as standalone book entries.
          </div>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <div className="border rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Date</th>
                    <th className="text-left p-2 font-medium">Party / Description</th>
                    <th className="text-right p-2 font-medium">Amount</th>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-right p-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.items.map((o, i) => {
                    const meta = TYPE_META[o.type] || { label: o.type, cls: 'bg-stone-100 text-stone-700' };
                    return (
                      <tr key={`${o.type}-${o.id}`} className="border-b last:border-b-0 hover:bg-muted/30" data-testid={`orphan-row-${i}`}>
                        <td className="p-2">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${meta.cls}`}>{meta.label}</span>
                        </td>
                        <td className="p-2 text-muted-foreground">{(o.date || '').slice(0, 10)}</td>
                        <td className="p-2">
                          <div className="font-medium truncate max-w-[280px]" title={o.party_name}>{o.party_name}</div>
                          <div className="text-[10px] text-muted-foreground">Deleted {(o.deleted_at || '').slice(0, 10)}</div>
                        </td>
                        <td className="p-2 text-right font-medium">{Number(o.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="p-2">
                          {o.can_rebind_to ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                              <Check size={10} /> Matching statement found
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" title={o.file_hash ? 'No statement with this file_hash currently exists' : 'No file_hash recorded — re-upload not auto-detectable'}>
                              <X size={10} /> No fresh match
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <div className="flex gap-1 justify-end">
                            {o.can_rebind_to && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px] border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                                disabled={busy}
                                onClick={() => onRebind(o)}
                                data-testid={`orphan-rebind-${i}`}
                                title="Re-link this record to the matching fresh statement"
                              >
                                Rebind
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              disabled={busy}
                              onClick={() => onDetach(o)}
                              data-testid={`orphan-detach-${i}`}
                              title="Keep this record as a standalone entry (no source statement)"
                            >
                              Detach
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            Tip: re-upload the same original file (same SHA-256) and the orphans will be re-linked automatically — you don&apos;t need to click Rebind manually. This list mostly helps when you uploaded a corrected/different file.
          </div>
        </CardContent>
      )}
    </Card>
  );
}
